import { ref } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';

/**
 * Confirming and posting a purchase-order chase action.
 *
 * Extracted from the PO Manager's List.vue when the invoice wizard's pick-jobs
 * step became the second consumer. The two screens must not be able to word the
 * same action differently — the invoice-without-a-PO confirmation in particular
 * is the one an operator acts on irreversibly, so it is lifted verbatim rather
 * than paraphrased per screen.
 *
 * The payload is whatever `PoReminderService::actionPayload()` produced for the
 * row: `{ id, stage, reference, label, email, buttonClass }`. Nothing here
 * derives the stage or the wording itself, so a screen cannot disagree with the
 * server about what a press does.
 */
export function usePoActions() {

    const working = ref(false);

    /**
     * Swal renders `html` as markup, and the address is customer-entered data
     * rather than anything this application generated.
     */
    const escapeHtml = (value) => String(value).replace(
        /[&<>"']/g,
        (character) => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
        })[character]
    );

    /**
     * The two actions are not the same kind of act and must not be confirmed
     * alike. A reminder is another wait; the other raises an invoice with no
     * purchase order on it and emails it, and cannot be undone — so it says so,
     * in those words, and its confirm button is the destructive colour.
     *
     * The wording says what happens to the customer, never "force fill" (D25,
     * 2026-09-01). The stage key stays `force_fill` because the payload
     * contract does; an operator should not have to know that.
     */
    const confirmThenProcess = (payload, done) => {
        if (working.value) {
            return;
        }

        const forceFill = payload.stage === 'force_fill';

        // Named, not "the customer": the address is worked out from the booking
        // contact, then accounts, then the invoice address, so which of a
        // customer's people actually receives this is not obvious from the row —
        // and it is the one thing worth checking before sending.
        const reminderBody = payload.email
            ? `<p class="mb-0">This emails <strong>${escapeHtml(payload.email)}</strong> asking for a `
                + `purchase order for <strong>${payload.reference}</strong>, and records the reminder `
                + 'against the job.</p>'
            : `<p class="mb-0">There is no contact address recorded for `
                + `<strong>${payload.reference}</strong>, so there is nobody to chase and the reminder `
                + 'will be refused.</p>';

        // Reworded for D25 (2026-09-01). It used to promise that the purchase
        // order would be set to NOT SUPPLIED, which was true while the service
        // wrote that literal reference and is a lie now that it leaves the
        // field empty. A confirmation describing a write that no longer happens
        // is worse than no confirmation: it is the one an operator trusts.
        const body = forceFill
            ? `<p class="mb-2">This raises an invoice for <strong>${payload.reference}</strong> `
                + 'with <strong>no purchase order on it</strong>, and '
                + '<strong>emails it to the customer immediately</strong>.</p>'
                + '<p class="mb-0">This cannot be undone.</p>'
            : reminderBody;

        Swal.fire({
            icon: forceFill ? 'warning' : 'question',
            title: forceFill
                ? `Invoice ${payload.reference} without a purchase order?`
                : `${payload.label}?`,
            html: body,
            showCancelButton: true,
            confirmButtonText: forceFill ? 'Yes, invoice without a PO' : 'Yes, send it',
            confirmButtonColor: forceFill ? '#dc3545' : undefined,
            cancelButtonText: 'Cancel',
            width: '600px',
        }).then((result) => {
            if (result.isConfirmed) {
                process(payload, done);
            }
        });
    };

    /**
     * One id through the same endpoint the bulk press will use — a single press
     * is a selection of one, so eligibility is re-checked server side either way.
     */
    const process = (payload, done) => {
        working.value = true;

        const data = new FormData();
        data.append('ids', String(payload.id));
        // The row was drawn with a stage and the operator has just confirmed
        // wording built from it, so that is the intent. Sent explicitly so the
        // endpoint refuses if the job became something else in between —
        // otherwise the confirmation describes one action and another happens
        // (Stage 17).
        data.append('intent', payload.stage === 'force_fill' ? 'invoice-without-po' : 'remind');

        axios.post(route('api.movements.po.process'), data)
            .then((response) => {
                working.value = false;
                reportOutcome(response.data);
                done();
            })
            .catch((error) => {
                working.value = false;
                reportError(error);
                done();
            });
    };

    /**
     * A refusal is normal, not an error: the job may have been chased by
     * someone else a moment ago. Those carry a reason worth reading, so they
     * get a modal rather than a toast that disappears.
     */
    const reportOutcome = (result) => {
        const messages = result.messages ?? [];

        if (messages.length > 0) {
            Swal.fire({
                icon: 'warning',
                // Not "nothing was sent": this composable now backs an invoice
                // action as well as a reminder, and a refused invoice is not a
                // letter that failed to go out.
                title: 'No action taken',
                html: `<ul class="text-start small mb-0">${messages.map((m) => `<li>${m}</li>`).join('')}</ul>`,
                width: '600px',
                confirmButtonText: 'Close',
            });

            return;
        }

        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            // `forced` is the response key, not a description of what happened:
            // nothing was filled in, the invoice simply went out without one.
            title: (result.forced ?? 0) > 0 ? 'Invoiced without a purchase order' : 'Reminder sent',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const reportError = (error) => {
        if (!error.response) {
            return;
        }

        let allErrors = '';

        if (error.response?.data?.errors) {
            Object.entries(error.response.data.errors).forEach(([, messages]) => {
                messages.forEach((message) => {
                    allErrors += '<p class="mb-0">' + message + '</p>';
                });
            });
        } else if (error.response?.data?.message) {
            allErrors = error.response.data.message;
        }

        Swal.fire({
            icon: 'error',
            title: 'Could not action that job',
            html: allErrors,
            width: '600px',
            confirmButtonText: 'Dismiss',
            confirmButtonColor: '#dc3545',
        });
    };

    return {
        working,
        escapeHtml,
        confirmThenProcess,
    };
}
